import { doc, getDocFromServer, increment, writeBatch } from "@firebase/firestore";
import { auth, db } from "./firebase";
import { diag } from "./diag";

/**
 * Posts and comments the server hasn't confirmed yet, kept on the device.
 *
 * Firestore keeps writes it couldn't send yet in memory only (its on-device
 * cache is off, see firebase.ts). A post written without a connection looked
 * posted, but was lost if the app was closed before the connection came back.
 * So every post and comment is noted here first and removed once the server
 * has it; whatever is left is sent again when the app starts, comes back to
 * the foreground or gets its connection back.
 *
 * Sending again is always safe: the document id is fixed, a document that is
 * already on the server is skipped, and a comment and its post's comment
 * count are written together (both or neither), so the count can't go up twice.
 */

const KEY = "nn-outbox-v1";
// Older than this: dropped (the person has long since noticed it's missing).
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export interface OutboxEntry {
  /** Document path, e.g. posts/abc or posts/abc/comments/def. */
  path: string;
  data: Record<string, unknown>;
  /** A post whose commentCount goes up by one with this document. */
  countPostId?: string;
  uid: string;
  at: number;
}

function load(): OutboxEntry[] {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

// Kept in memory too, so screens can ask "is this still sending?" cheaply
// (and so it still works if storage is full or unavailable).
let current: OutboxEntry[] | null = null;
const listeners = new Set<() => void>();

function entries(): OutboxEntry[] {
  if (!current) current = load();
  return current;
}

function save(next: OutboxEntry[]) {
  current = next;
  try {
    if (next.length) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage full: the write still goes through while the app is open.
  }
  listeners.forEach((listener) => listener());
}

/** Called whenever something is added to or leaves the outbox. */
export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Everything waiting to be sent (the same array until something changes). */
export function outboxSnapshot(): OutboxEntry[] {
  return entries();
}

// Written in this session and not confirmed yet: Firestore is still sending
// these itself, so a flush must leave them alone (a comment count would go
// up twice).
const inFlight = new Set<string>();

/**
 * Forgets a post or comment (and the comments under a post) that should no
 * longer be sent, e.g. because it was deleted. Returns true if it only ever
 * existed on this device, so there is nothing to delete on the server.
 */
export function discardFromOutbox(path: string): boolean {
  const matches = (entry: OutboxEntry) =>
    entry.path === path || entry.path.startsWith(`${path}/`);
  const own = entries().find((entry) => entry.path === path);
  if (entries().some(matches)) save(entries().filter((entry) => !matches(entry)));
  return !!own && !inFlight.has(path);
}

function remove(path: string) {
  if (entries().some((entry) => entry.path === path)) {
    save(entries().filter((entry) => entry.path !== path));
  }
}

/** The write for an entry: the document, plus the comment count if any. */
function commit(entry: Pick<OutboxEntry, "path" | "data" | "countPostId">): Promise<void> {
  const batch = writeBatch(db);
  batch.set(doc(db, entry.path), entry.data);
  if (entry.countPostId) {
    batch.update(doc(db, "posts", entry.countPostId), { commentCount: increment(1) });
  }
  return batch.commit();
}

/**
 * Writes a post or comment, keeping a copy on the device until the server
 * confirms it. Returns the write (it resolves once the server has it).
 */
export function writeWithOutbox(entry: Pick<OutboxEntry, "path" | "data" | "countPostId">): Promise<void> {
  const uid = auth.currentUser?.uid ?? "";
  save([...entries().filter((e) => e.path !== entry.path), { ...entry, uid, at: Date.now() }]);
  const write = commit(entry);
  inFlight.add(entry.path);
  write.then(
    () => {
      inFlight.delete(entry.path);
      remove(entry.path);
    },
    (error) => {
      inFlight.delete(entry.path);
      // Refused by the server (not a connection problem): sending it again
      // would be refused too.
      diag("outbox write refused", { path: entry.path, code: (error as { code?: string })?.code });
      remove(entry.path);
    },
  );
  return write;
}

let flushing = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = 0;

// Firestore needs a few seconds to reconnect after the connection returns,
// so a flush that found no connection tries again, waiting longer each time.
function retryLater() {
  if (retryTimer) return;
  retryDelay = Math.min(retryDelay ? retryDelay * 2 : 3000, 60_000);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushOutbox("retry");
  }, retryDelay);
}

/** Sends whatever an earlier session couldn't. Safe to call any time. */
export async function flushOutbox(reason: string): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid || flushing) return;
  // The connection just came back (or the app did): retry soon again.
  if (reason !== "retry") {
    retryDelay = 0;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  }
  const now = Date.now();
  const fresh = entries().filter((entry) => now - entry.at < MAX_AGE_MS);
  if (fresh.length !== entries().length) save(fresh);
  const mine = fresh.filter((entry) => entry.uid === uid && !inFlight.has(entry.path));
  if (!mine.length) {
    retryDelay = 0;
    return;
  }
  flushing = true;
  let stopped = false;
  diag("outbox flush", { reason, count: mine.length });
  try {
    for (const entry of mine) {
      // Deleted while we were sending the ones before it.
      if (!entries().some((e) => e.path === entry.path)) continue;
      try {
        const existing = await getDocFromServer(doc(db, entry.path));
        if (!existing.exists()) {
          await commit(entry);
          diag("outbox sent", { path: entry.path });
        }
        remove(entry.path);
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (code === "permission-denied" || code === "invalid-argument" || code === "not-found") {
          // e.g. the post a comment belongs to was deleted meanwhile.
          diag("outbox entry dropped", { path: entry.path, code });
          remove(entry.path);
        } else {
          // Still no connection: try again a bit later.
          diag("outbox flush stopped", { code });
          stopped = true;
          break;
        }
      }
    }
  } finally {
    flushing = false;
  }
  if (stopped) retryLater();
  else retryDelay = 0;
}

let started = false;

/** Flushes now and whenever the app returns or gets its connection back. */
export function startOutbox() {
  if (started) return;
  started = true;
  const flush = (reason: string) => () => void flushOutbox(reason);
  window.addEventListener("online", flush("online"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flushOutbox("app visible");
  });
  auth.onAuthStateChanged((user) => {
    if (user) void flushOutbox("start");
  });
}
